/**
 * **Reading the site's footer out of the published `siteSettings` entry** (`CMS-014`), for every
 * app that draws the site footer.
 *
 * These began in `apps/web/src/domains/cms/constants.ts`, when the marketing site and its member
 * app were the only hosts of the footer. The organizer entry page is the third (`ORG-001` AC-5,
 * TW 44108052), and it is a different application: a second copy of these readers there would be
 * a second answer to *what does the footer show when the editor said nothing*, which is the half of
 * `CMS-014` AC-3 that quietly stops being true. So they moved here, and `apps/web` re-exports them
 * under the names it already used.
 *
 * No React and no HTTP: the entry is whatever `GET /api/cms/content/siteSettings` answered, and
 * every reader here survives it being absent or the wrong shape.
 */
export const DEFAULT_SITE_NAME = 'H2HSpark';
/** The address the site falls back to for "Contact". It is the one the prototype's footer used. */
export const DEFAULT_CONTACT_EMAIL_ADDRESS = 'hello@h2hspark.com';
/** The footer's shape when nobody has set one — the prototype's columns, links only. */
export const DEFAULT_FOOTER_GROUPS = [
    {
        heading: 'Members',
        links: [
            { label: 'Browse events', href: '/app' },
            { label: 'Log in', href: '/app/login' },
        ],
    },
    {
        heading: 'Organizers',
        links: [{ label: 'Host with H2HSpark', href: '/for-organizers' }],
    },
];
/** A string field, or `''`. */
export function siteSettingsText(data, key) {
    const value = data[key];
    return typeof value === 'string' ? value : '';
}
/** A list field, or an empty list. */
export function siteSettingsList(data, key) {
    const value = data[key];
    return Array.isArray(value) ? value : [];
}
/** A string setting, or `fallback` when the entry is absent or the field is empty. */
export function siteSettingOr(settings, key, fallback) {
    if (settings === undefined)
        return fallback;
    const value = siteSettingsText(settings, key);
    return value === '' ? fallback : value;
}
export function siteName(settings) {
    return siteSettingOr(settings, 'siteName', DEFAULT_SITE_NAME);
}
export function siteFooterText(settings) {
    return siteSettingOr(settings, 'footerText', '');
}
export function siteFooterNote(settings) {
    return siteSettingOr(settings, 'footerNote', '');
}
export function siteContactEmailAddress(settings) {
    return siteSettingOr(settings, 'contactEmailAddress', DEFAULT_CONTACT_EMAIL_ADDRESS);
}
export function siteContactLocation(settings) {
    return siteSettingOr(settings, 'contactLocation', '');
}
/**
 * The footer's link groups, in the editor's order. A heading with no links under it is dropped —
 * an empty column reads as a rendering failure — and no usable group at all means the defaults.
 */
export function siteFooterGroups(settings) {
    if (settings === undefined)
        return DEFAULT_FOOTER_GROUPS;
    const groups = siteSettingsList(settings, 'footerGroups')
        .map((group) => ({
        heading: siteSettingsText(group, 'heading'),
        links: siteSettingsList(group, 'links')
            .map((link) => ({
            label: siteSettingsText(link, 'label'),
            href: siteSettingsText(link, 'href'),
        }))
            .filter((link) => link.label !== '' && link.href !== ''),
    }))
        .filter((group) => group.heading !== '' && group.links.length > 0);
    return groups.length > 0 ? groups : DEFAULT_FOOTER_GROUPS;
}
/** No default — a guess at where H2HSpark is on LinkedIn would be somebody else's page. */
export function siteSocialLinks(settings) {
    if (settings === undefined)
        return [];
    return siteSettingsList(settings, 'socialLinks')
        .map((link) => ({
        platform: siteSettingsText(link, 'platform'),
        icon: siteSettingsText(link, 'icon'),
        url: siteSettingsText(link, 'url'),
    }))
        .filter((link) => link.platform !== '' && link.url !== '');
}
/** The prefix content uses to name an organizer-console page (see `apps/web`'s `ContentLink`). */
export const SITE_ORGANIZER_LINK_PREFIX = 'organizer:';
/** The console pages content may name: the public entry page and sign-in. */
export const SITE_ORGANIZER_LINK_PATHS = ['/start', '/login'];
/**
 * An address an editor typed into the site settings, made absolute **for an app that is not the
 * marketing site** — today the organizer console, which draws the site footer on its entry page.
 *
 * - `organizer:/start` and `organizer:/login` resolve against the console's own base.
 * - A site-relative path (`/contact`) belongs to the marketing site, so it resolves against
 *   `siteUrl`. Rendered as-is on another app's origin it would open a route that app does not have.
 * - `http:`, `https:` and `mailto:` pass through.
 * - Anything else — `javascript:`, `data:`, an unknown console path, a protocol-relative `//host` —
 *   is `null`, and the caller renders the label as text. The same allow-list the marketing site's
 *   `safeHref` holds.
 */
export function resolveSiteLinkForAnotherApp(href, bases) {
    const address = href.trim();
    if (address.startsWith(SITE_ORGANIZER_LINK_PREFIX)) {
        const path = address.slice(SITE_ORGANIZER_LINK_PREFIX.length);
        return SITE_ORGANIZER_LINK_PATHS.includes(path)
            ? `${bases.organizerUrl.replace(/\/+$/u, '')}${path}`
            : null;
    }
    if (address.startsWith('/') && !address.startsWith('//')) {
        return `${bases.siteUrl.replace(/\/+$/u, '')}${address}`;
    }
    return /^(?:https?:\/\/|mailto:)/iu.test(address) ? address : null;
}
/**
 * **What the organizer header shows when the editor said nothing** — the 16 October design on
 * TW 44435875, transcribed: *"Attending events? Go to H2HSpark for members"* over the header, and
 * Sign in, Sign up and Book a demo at its right. Sign in and Sign up are the console's two public
 * doors (`SITE_ORGANIZER_LINK_PATHS`), Book a demo is `CMS-024`'s page.
 */
export const DEFAULT_ORGANIZER_HEADER = {
    barText: 'Attending events?',
    barLinkLabel: 'Go to H2HSpark for members',
    barLinkHref: '/',
    actions: [
        { label: 'Sign in', href: 'organizer:/login', style: 'link', icon: '' },
        { label: 'Sign up', href: 'organizer:/start', style: 'outline', icon: '' },
        { label: 'Book a demo', href: '/book-a-demo', style: 'primary', icon: 'CalendarPlus' },
    ],
    signedInLabel: 'Go to your console',
};
const HEADER_ACTION_STYLES = ['link', 'primary', 'outline'];
/**
 * The header a published marketing page asked for. Anything but `organizer` — nothing, an empty
 * select, a value from a later release — is `standard`, so every page published before the field
 * existed keeps the header it had (`CMS-025` AC-1).
 */
export function marketingPageHeader(page) {
    return page !== undefined && siteSettingsText(page, 'header') === 'organizer'
        ? 'organizer'
        : 'standard';
}
/**
 * **The organizer header, read out of the published `siteSettings`** (`CMS-025` AC-3), for both
 * apps that draw it — the marketing site and the organizer console's signed-out screens.
 *
 * Each half falls back on its own: an editor who set the bar and no actions gets the default
 * actions, because a header with no way in is the one state it must never be in. An action
 * without a label or a link is dropped, and one whose style is not a style is drawn as a link.
 */
export function siteOrganizerHeader(settings) {
    if (settings === undefined)
        return DEFAULT_ORGANIZER_HEADER;
    const actions = siteSettingsList(settings, 'organizerNavigation')
        .map((item) => {
        const style = siteSettingsText(item, 'style');
        return {
            label: siteSettingsText(item, 'label'),
            href: siteSettingsText(item, 'href'),
            style: HEADER_ACTION_STYLES.includes(style)
                ? style
                : 'link',
            icon: siteSettingsText(item, 'icon'),
        };
    })
        .filter((item) => item.label !== '' && item.href !== '');
    return {
        barText: siteSettingOr(settings, 'organizerBarText', DEFAULT_ORGANIZER_HEADER.barText),
        barLinkLabel: siteSettingOr(settings, 'organizerBarLinkLabel', DEFAULT_ORGANIZER_HEADER.barLinkLabel),
        barLinkHref: siteSettingOr(settings, 'organizerBarLinkHref', DEFAULT_ORGANIZER_HEADER.barLinkHref),
        actions: actions.length > 0 ? actions : DEFAULT_ORGANIZER_HEADER.actions,
        signedInLabel: siteSettingOr(settings, 'organizerSignedInLabel', DEFAULT_ORGANIZER_HEADER.signedInLabel),
    };
}
//# sourceMappingURL=site-settings.js.map